import { getRetryAfterMs } from 'pi-kiosk-shared';
import { readRequestId, setClientCorrelationId } from 'pi-kiosk-shared/logging';
import { setSentryCorrelationId } from 'pi-kiosk-shared/sentry';
import { getEnvironmentConfig } from '../../config/environments.js';
import i18n from '../../shared/i18n/i18n.js';
import { isAdminStepUpRequiredError } from '../../features/auth/step-up/adminStepUpProof.js';
import { notifyAdminStepUpRequired } from '../../features/auth/step-up/adminStepUpRequiredBus.js';
import {
  DEMO_TOUR_WRITE_DENIED_CODE,
  notifyDemoTourWriteDenied,
} from '../../features/account/demoTour/demoTourWriteDeniedBus.js';
import {
  clearDenied,
  denyKeyForApiPath,
  entitlementDenyKindsFromEnvelopeDetails,
  isAdminMeRouteFamily,
  isDenied,
  markDenied,
  routeFamilyFromApiPath,
  shouldMarkApiDenied,
} from '../../shared/http/apiDenyCache.js';
import {
  normalizeAdminOutboundSourceKey,
  scheduleAdminOutbound,
} from '../../shared/http/adminOutboundFairQueue.js';
import {
  HttpStormLatchedError,
  stormAwareFetch,
} from '../../shared/http/stormAwareFetch.js';
import {
  buildRequestFailureCacheKey,
  clearRequestFailure,
  markRequestFailure,
  throwIfCachedFailure,
} from '../../shared/http/requestFailureCache.js';
import { buildTenantApiBase, withTenantInPath } from '../../shared/tenant.js';
import {
  adminAuthCsrfHeaders,
  shouldAttachAdminAuthCsrf,
} from '../../shared/session/adminAuthCsrf.js';
import { isHardDefinitiveRefreshRevocation } from '../../shared/session/adminRefreshCatalog.js';
import {
  attemptAdminSessionFromCookies,
  isMissingCredentialUnauthorized,
  shouldAttemptRefreshOn401,
  shouldAttemptRefreshOnMissingCredential,
} from '../../shared/session/adminRefreshCoordinator.js';
import { endAdminSessionGlobal } from '../../shared/session/endAdminSession.js';
import {
  ApiError,
  messageFromApiErrorEnvelope,
  type ApiErrorEnvelope,
  type ApiResponse,
  type RequestAuthMode,
} from './apiClient.types.js';

/**
 * Wave D — soft-stop when latched; arm latch from HTTP_STORM_DETECTED.
 * Maps HttpStormLatchedError → ApiError for existing callers/SWR.
 */
async function adminStormAwareFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  try {
    return await stormAwareFetch(input, init);
  } catch (err) {
    if (err instanceof HttpStormLatchedError) {
      throw new ApiError(
        429,
        { success: false, code: err.code },
        err.message,
        err.code,
        err.retryAfterMs,
      );
    }
    throw err;
  }
}

/** Fresh UUID for outbound `X-Request-ID` (ADM-TKT-0204). Prefer at raw fetch call sites. */
export function createXRequestId(): string {
  return crypto.randomUUID();
}

/** Coalesce identical in-flight GETs (same resolved tenant path + auth mode). */
const inFlightGets = new Map<string, Promise<ApiResponse<unknown>>>();

function retryAfterMsFromHttpResponse(response: Response): number | undefined {
  if (!response.headers.has('retry-after') && !response.headers.has('ratelimit-reset')) {
    return undefined;
  }
  return getRetryAfterMs({ response });
}

function throwIfDenied(denyKey: string): void {
  if (!isDenied(denyKey)) {
    return;
  }
  throw new ApiError(
    403,
    { success: false, code: 'API_DENY_CACHED' },
    'Skipping denied request (in-session)',
    'API_DENY_CACHED',
  );
}

/**
 * Session deny cache may stick after a transient 403 during auth settle.
 * Allow one bypass per outbound call chain so a later GET can prove reachability.
 */
function bypassDeniedOnceOrThrow(denyKey: string, denyBypassUsed: boolean): boolean {
  if (!isDenied(denyKey)) {
    return denyBypassUsed;
  }
  if (denyBypassUsed) {
    throwIfDenied(denyKey);
  }
  clearDenied(denyKey);
  return true;
}

function throwFromFailedResponse(
  response: Response,
  errorData: ApiErrorEnvelope,
  denyKey: string,
  method = 'GET',
  correlationId?: string,
  apiPath?: string,
): never {
  const parts = denyKey.split('|');
  const routeFamily =
    apiPath !== undefined
      ? routeFamilyFromApiPath(apiPath)
      : (parts.length > 0 ? parts[parts.length - 1] : denyKey) ?? denyKey;
  // Package F §3.4 — permanent deny uses code + structured kinds (raw wire kinds;
  // unknown/mixed non-permanent kinds must not poison the cache).
  const entitlementDenyKinds = entitlementDenyKindsFromEnvelopeDetails(
    errorData.details,
  );
  if (
    shouldMarkApiDenied(
      response.status,
      errorData.code,
      method,
      routeFamily,
      entitlementDenyKinds,
    )
  ) {
    markDenied(denyKey);
  }
  const apiError = new ApiError(
    response.status,
    errorData,
    messageFromApiErrorEnvelope(errorData, response.status),
    errorData.code,
    retryAfterMsFromHttpResponse(response),
    correlationId,
  );
  // Appendix D′ — open global step-up dialog on ADMIN_STEP_UP_REQUIRED / 403 step-up.
  if (response.status === 403 && isAdminStepUpRequiredError(apiError)) {
    notifyAdminStepUpRequired();
  }
  // D4 demo-tour write deny — toast via bus (mirror is not auth SSOT).
  if (response.status === 403 && errorData.code === DEMO_TOUR_WRITE_DENIED_CODE) {
    notifyDemoTourWriteDenied();
  }
  throw apiError;
}

/**
 * G4 401 → cookie refresh. Soft failures do not end session; hard → endAdminSessionGlobal.
 * Returns true when the caller should retry the original request once.
 *
 * Missing-credential (`No token provided`) skips refresh only on anonymous cold boot.
 * Established SPA / dual Bearer sessions still rotate — access cookie expiry uses the
 * same BE message while the refresh cookie may still be valid.
 */
async function tryRecoverSessionAfter401(
  path: string,
  responseCode: string | undefined,
  responseMessage?: string,
): Promise<boolean> {
  if (!shouldAttemptRefreshOn401(path)) {
    return false;
  }
  if (isMissingCredentialUnauthorized(responseCode, responseMessage)) {
    if (!shouldAttemptRefreshOnMissingCredential()) {
      return false;
    }
  }
  // Hard revoke on the original API response — do not thrash refresh.
  // Cookies may still exist → best-effort logout API after local clear (G1 order).
  if (isHardDefinitiveRefreshRevocation(responseCode)) {
    await endAdminSessionGlobal({ broadcast: true, callApi: true });
    return false;
  }

  const refreshed = await attemptAdminSessionFromCookies({
    force: false,
    source: 'cookie-refresh',
  });
  if (refreshed.ok) {
    return true;
  }
  // H1 — hard on refreshed.code BEFORE soft residual (S8 bare 401 must not mask
  // REFRESH_TOKEN_MISSING / INVALID_REFRESH_TOKEN). Soft (S1–S8) → no end.
  // After failed rotate, BE already cleared cookies on REFRESH_TOKEN_MISSING —
  // skip logout API wait (callApi:false); local clear + redirect still run.
  if (isHardDefinitiveRefreshRevocation(refreshed.code)) {
    await endAdminSessionGlobal({ broadcast: true, callApi: false });
  }
  return false;
}

function abortSignalFromOptions(options: RequestInit | undefined): AbortSignal | undefined {
  const signal = options?.signal;
  return signal instanceof AbortSignal ? signal : undefined;
}

function authPartForMode(authMode: RequestAuthMode): string {
  if (authMode.kind === 'none') {
    return 'none';
  }
  if (authMode.kind === 'bearer') {
    return `bearer:${authMode.token}`;
  }
  return 'default';
}

export class ApiClientCore {
  constructor(private readonly getToken: () => string | null) {}

  private resolveBaseUrl(): string {
    return buildTenantApiBase(getEnvironmentConfig().apiUrl);
  }

  private authorizationForMode(authMode: RequestAuthMode): Record<string, string> {
    if (authMode.kind === 'none') {
      return {};
    }
    if (authMode.kind === 'bearer') {
      return { Authorization: `Bearer ${authMode.token}` };
    }
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Unqueued network work. Callers must wrap via {@link scheduleAdminOutbound}
   * so fair-queue timing applies; GET inFlightGets coalesce stays inside the slot.
   */
  private async executeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    skipTenantPrefix = false,
    authMode: RequestAuthMode = { kind: 'default' },
    retried = false,
    denyBypassUsed = false,
  ): Promise<ApiResponse<T>> {
    const tenantEndpoint = skipTenantPrefix ? endpoint : withTenantInPath(endpoint);
    const method = (options.method ?? 'GET').toUpperCase();
    const denyKey = denyKeyForApiPath(tenantEndpoint, method);
    const denyBypassed = bypassDeniedOnceOrThrow(denyKey, denyBypassUsed);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Request-ID': createXRequestId(),
      'Accept-Language': i18n.resolvedLanguage ?? i18n.language ?? 'cs',
      ...this.authorizationForMode(authMode),
      ...(shouldAttachAdminAuthCsrf(method, tenantEndpoint)
        ? adminAuthCsrfHeaders()
        : {}),
      ...(options.headers as Record<string, string> | undefined),
    };

    const response = await adminStormAwareFetch(`${this.resolveBaseUrl()}${tenantEndpoint}`, {
      ...options,
      // Cookie session (dual|cookie) — always include credentials on admin API.
      credentials: options.credentials ?? 'include',
      headers,
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as ApiErrorEnvelope;
      const correlationId =
        readRequestId(response) ??
        (typeof errorData.requestId === 'string' ? errorData.requestId : undefined);
      if (correlationId !== undefined) {
        setClientCorrelationId(correlationId);
        setSentryCorrelationId(correlationId);
      }
      if (response.status === 401 && !retried) {
        const shouldRetry = await tryRecoverSessionAfter401(
          tenantEndpoint,
          errorData.code,
          messageFromApiErrorEnvelope(errorData, response.status),
        );
        if (shouldRetry) {
          return this.executeRequest<T>(
            endpoint,
            options,
            skipTenantPrefix,
            authMode,
            true,
            denyBypassed,
          );
        }
      }
      throwFromFailedResponse(response, errorData, denyKey, method, correlationId, tenantEndpoint);
    }

    const okId = readRequestId(response);
    if (okId !== undefined) {
      setClientCorrelationId(okId);
      setSentryCorrelationId(okId);
    }

    if (denyBypassed) {
      clearDenied(denyKey);
    }

    return response.json() as Promise<ApiResponse<T>>;
  }

  private scheduleOutbound<T>(
    method: string,
    path: string,
    signal: AbortSignal | undefined,
    run: () => Promise<T>,
    onStart?: () => void
  ): Promise<T> {
    // Ensure source-key mapping is applied (scheduleAdminOutbound normalizes again for lanes/caps).
    normalizeAdminOutboundSourceKey(method, path);
    return scheduleAdminOutbound({ method, path, signal, onStart, run });
  }

  async get<T>(
    endpoint: string,
    skipTenantPrefix = false,
    authMode: RequestAuthMode = { kind: 'default' },
    requestOptions?: RequestInit & { readonly onStart?: () => void }
  ): Promise<ApiResponse<T>> {
    const tenantEndpoint = skipTenantPrefix ? endpoint : withTenantInPath(endpoint);
    const denyKey = denyKeyForApiPath(tenantEndpoint, 'GET');

    const dedupeKey = buildRequestFailureCacheKey([
      'GET',
      tenantEndpoint,
      authPartForMode(authMode),
    ]);
    throwIfCachedFailure(dedupeKey);

    const { onStart, ...fetchInit } = requestOptions ?? {};
    return this.scheduleOutbound(
      'GET',
      tenantEndpoint,
      abortSignalFromOptions(fetchInit),
      () => {
        // inFlightGets coalesce INSIDE the running fair-queue slot.
        const existing = inFlightGets.get(dedupeKey);
        if (existing !== undefined) {
          return existing as Promise<ApiResponse<T>>;
        }

        const pending = this.executeRequest<T>(
          endpoint,
          {
            ...fetchInit,
            method: 'GET',
          },
          skipTenantPrefix,
          authMode
        )
          .then((response) => {
            // Success proves the route is reachable for this session — clear both
            // negative caches. requestFailure was already asymmetric; leaving
            // apiDenyCache sticky after a racing 401→deny + in-flight 200 caused
            // mid-settle Branding chrome (`API_DENY_CACHED`) while later GETs 200.
            clearRequestFailure(dedupeKey);
            clearDenied(denyKey);
            return response;
          })
          .catch((error: unknown) => {
            if (error instanceof ApiError) {
              const routeFamily = routeFamilyFromApiPath(tenantEndpoint);
              // AuthProvider-owned boot /me 401 is expected while logged out (G3: at most
              // one cold-login identity probe) — do not TTL-poison establish.
              if (
                !(
                  error.statusCode === 401 &&
                  isAdminMeRouteFamily(routeFamily)
                )
              ) {
                markRequestFailure(dedupeKey, error.statusCode);
              }
            }
            throw error;
          })
          .finally(() => {
            inFlightGets.delete(dedupeKey);
          });

        inFlightGets.set(dedupeKey, pending as Promise<ApiResponse<unknown>>);
        return pending;
      },
      onStart
    );
  }

  async post<T>(
    endpoint: string,
    data: unknown,
    skipTenantPrefix = false,
    authMode: RequestAuthMode = { kind: 'default' },
    requestOptions?: RequestInit
  ): Promise<ApiResponse<T>> {
    const tenantEndpoint = skipTenantPrefix ? endpoint : withTenantInPath(endpoint);
    return this.scheduleOutbound(
      'POST',
      tenantEndpoint,
      abortSignalFromOptions(requestOptions),
      () =>
        this.executeRequest<T>(
          endpoint,
          {
            ...requestOptions,
            method: 'POST',
            body: JSON.stringify(data),
          },
          skipTenantPrefix,
          authMode
        )
    );
  }

  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    skipTenantPrefix = false,
    authMode: RequestAuthMode = { kind: 'default' },
    extraHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const tenantEndpoint = skipTenantPrefix ? endpoint : withTenantInPath(endpoint);
    const denyKey = denyKeyForApiPath(tenantEndpoint, 'POST');

    return this.scheduleOutbound('POST', tenantEndpoint, undefined, async () => {
      const runOnce = async (retried: boolean, denyBypassUsed = false): Promise<ApiResponse<T>> => {
        const denyBypassed = bypassDeniedOnceOrThrow(denyKey, denyBypassUsed);
        const headers: Record<string, string> = {
          'X-Request-ID': createXRequestId(),
          'Accept-Language': i18n.resolvedLanguage ?? i18n.language ?? 'cs',
          ...this.authorizationForMode(authMode),
          ...(shouldAttachAdminAuthCsrf('POST', tenantEndpoint)
            ? adminAuthCsrfHeaders()
            : {}),
          ...extraHeaders,
        };

        const response = await adminStormAwareFetch(`${this.resolveBaseUrl()}${tenantEndpoint}`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: formData,
        });

        if (!response.ok) {
          const errorData = (await response.json().catch(() => ({}))) as ApiErrorEnvelope;
          const correlationId =
            readRequestId(response) ??
            (typeof errorData.requestId === 'string' ? errorData.requestId : undefined);
          if (correlationId !== undefined) {
            setClientCorrelationId(correlationId);
            setSentryCorrelationId(correlationId);
          }
          if (response.status === 401 && !retried) {
            const shouldRetry = await tryRecoverSessionAfter401(
              tenantEndpoint,
              errorData.code,
              messageFromApiErrorEnvelope(errorData, response.status),
            );
            if (shouldRetry) {
              return runOnce(true, denyBypassed);
            }
          }
          throwFromFailedResponse(response, errorData, denyKey, 'POST', correlationId, tenantEndpoint);
        }

        const okId = readRequestId(response);
        if (okId !== undefined) {
          setClientCorrelationId(okId);
          setSentryCorrelationId(okId);
        }

        if (denyBypassed) {
          clearDenied(denyKey);
        }

        return response.json() as Promise<ApiResponse<T>>;
      };

      return runOnce(false);
    });
  }

  async putFormData<T>(
    endpoint: string,
    formData: FormData,
    skipTenantPrefix = false,
    authMode: RequestAuthMode = { kind: 'default' },
    extraHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const tenantEndpoint = skipTenantPrefix ? endpoint : withTenantInPath(endpoint);
    const denyKey = denyKeyForApiPath(tenantEndpoint, 'PUT');

    return this.scheduleOutbound('PUT', tenantEndpoint, undefined, async () => {
      const runOnce = async (retried: boolean, denyBypassUsed = false): Promise<ApiResponse<T>> => {
        const denyBypassed = bypassDeniedOnceOrThrow(denyKey, denyBypassUsed);
        const headers: Record<string, string> = {
          'X-Request-ID': createXRequestId(),
          'Accept-Language': i18n.resolvedLanguage ?? i18n.language ?? 'cs',
          ...this.authorizationForMode(authMode),
          ...(shouldAttachAdminAuthCsrf('PUT', tenantEndpoint)
            ? adminAuthCsrfHeaders()
            : {}),
          ...extraHeaders,
        };

        const response = await adminStormAwareFetch(`${this.resolveBaseUrl()}${tenantEndpoint}`, {
          method: 'PUT',
          credentials: 'include',
          headers,
          body: formData,
        });

        if (!response.ok) {
          const errorData = (await response.json().catch(() => ({}))) as ApiErrorEnvelope;
          const correlationId =
            readRequestId(response) ??
            (typeof errorData.requestId === 'string' ? errorData.requestId : undefined);
          if (correlationId !== undefined) {
            setClientCorrelationId(correlationId);
            setSentryCorrelationId(correlationId);
          }
          if (response.status === 401 && !retried) {
            const shouldRetry = await tryRecoverSessionAfter401(
              tenantEndpoint,
              errorData.code,
              messageFromApiErrorEnvelope(errorData, response.status),
            );
            if (shouldRetry) {
              return runOnce(true, denyBypassed);
            }
          }
          throwFromFailedResponse(response, errorData, denyKey, 'PUT', correlationId, tenantEndpoint);
        }

        const putOkId = readRequestId(response);
        if (putOkId !== undefined) {
          setClientCorrelationId(putOkId);
          setSentryCorrelationId(putOkId);
        }

        if (denyBypassed) {
          clearDenied(denyKey);
        }

        return response.json() as Promise<ApiResponse<T>>;
      };

      return runOnce(false);
    });
  }

  async put<T>(
    endpoint: string,
    data: unknown,
    skipTenantPrefix = false,
    authMode: RequestAuthMode = { kind: 'default' }
  ): Promise<ApiResponse<T>> {
    const tenantEndpoint = skipTenantPrefix ? endpoint : withTenantInPath(endpoint);
    return this.scheduleOutbound('PUT', tenantEndpoint, undefined, () =>
      this.executeRequest<T>(
        endpoint,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        },
        skipTenantPrefix,
        authMode
      )
    );
  }

  async patch<T>(
    endpoint: string,
    data: unknown,
    skipTenantPrefix = false,
    authMode: RequestAuthMode = { kind: 'default' }
  ): Promise<ApiResponse<T>> {
    const tenantEndpoint = skipTenantPrefix ? endpoint : withTenantInPath(endpoint);
    return this.scheduleOutbound('PATCH', tenantEndpoint, undefined, () =>
      this.executeRequest<T>(
        endpoint,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        },
        skipTenantPrefix,
        authMode
      )
    );
  }

  async delete<T>(
    endpoint: string,
    skipTenantPrefix = false,
    authMode: RequestAuthMode = { kind: 'default' },
    extraHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const tenantEndpoint = skipTenantPrefix ? endpoint : withTenantInPath(endpoint);
    return this.scheduleOutbound('DELETE', tenantEndpoint, undefined, () =>
      this.executeRequest<T>(
        endpoint,
        { method: 'DELETE', headers: extraHeaders },
        skipTenantPrefix,
        authMode
      )
    );
  }

  /**
   * Binary GET (e.g. LegalDocument download). Same auth / deny / 401 refresh as JSON GETs.
   */
  async getBlob(
    endpoint: string,
    skipTenantPrefix = false,
    authMode: RequestAuthMode = { kind: 'default' },
  ): Promise<{
    blob: Blob;
    contentType: string;
    checksum: string | null;
    contentDisposition: string | null;
  }> {
    const tenantEndpoint = skipTenantPrefix ? endpoint : withTenantInPath(endpoint);
    const denyKey = denyKeyForApiPath(tenantEndpoint, 'GET');

    return this.scheduleOutbound('GET', tenantEndpoint, undefined, async () => {
      const runOnce = async (retried: boolean, denyBypassUsed = false): Promise<{
        blob: Blob;
        contentType: string;
        checksum: string | null;
        contentDisposition: string | null;
      }> => {
        const denyBypassed = bypassDeniedOnceOrThrow(denyKey, denyBypassUsed);
        const headers: Record<string, string> = {
          'X-Request-ID': createXRequestId(),
          'Accept-Language': i18n.resolvedLanguage ?? i18n.language ?? 'cs',
          ...this.authorizationForMode(authMode),
          ...(shouldAttachAdminAuthCsrf('GET', tenantEndpoint)
            ? adminAuthCsrfHeaders()
            : {}),
        };

        const response = await adminStormAwareFetch(`${this.resolveBaseUrl()}${tenantEndpoint}`, {
          method: 'GET',
          credentials: 'include',
          headers,
        });

        if (!response.ok) {
          const errorData = (await response.json().catch(() => ({}))) as ApiErrorEnvelope;
          const correlationId =
            readRequestId(response) ??
            (typeof errorData.requestId === 'string' ? errorData.requestId : undefined);
          if (correlationId !== undefined) {
            setClientCorrelationId(correlationId);
            setSentryCorrelationId(correlationId);
          }
          if (response.status === 401 && !retried) {
            const shouldRetry = await tryRecoverSessionAfter401(
              tenantEndpoint,
              errorData.code,
              messageFromApiErrorEnvelope(errorData, response.status),
            );
            if (shouldRetry) {
              return runOnce(true, denyBypassed);
            }
          }
          throwFromFailedResponse(response, errorData, denyKey, 'GET', correlationId, tenantEndpoint);
        }

        const okId = readRequestId(response);
        if (okId !== undefined) {
          setClientCorrelationId(okId);
          setSentryCorrelationId(okId);
        }

        if (denyBypassed) {
          clearDenied(denyKey);
        }

        const blob = await response.blob();
        return {
          blob,
          contentType: response.headers.get('Content-Type') ?? blob.type ?? 'application/octet-stream',
          checksum: response.headers.get('X-Content-Checksum-SHA256'),
          contentDisposition: response.headers.get('Content-Disposition'),
        };
      };

      return runOnce(false);
    });
  }
}
